import React from "react";
import { Column } from "@/components/Datatable/types";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import EventSessionActionMenu from "@/modules/eventRegistration/features/EventSessionManagement/components/EventSessionActionMenu/EventSessionActionMenu";
import { SessionSummary } from "@/modules/eventRegistration/features/EventSessionManagement/api/fetchEventSessions/types";
import { formatSessionDateTime } from "@/modules/eventRegistration/shared/utils/dateFormatting";

interface CreateEventSessionColumnsProps {
  onDeleteSession: (uuid: string) => void;
  onCancelSession?: (uuid: string) => void;
  onEditSession?: (uuid: string) => void;
  onTogglePublishedSession: (uuid: string, newVal: boolean) => void;
}

export function createEventSessionColumns({
  onDeleteSession,
  onCancelSession,
  onEditSession,
  onTogglePublishedSession,
}: CreateEventSessionColumnsProps): Column<SessionSummary>[] {
  return [
    {
      header: "Session Name",
      accessorKey: "name",
      enableSorting: true,
      cell: ({ row }) => row.original.name || "--",
    },
    {
      header: "Start Date",
      accessorKey: "startDate",
      enableSorting: true,
      cell: ({ row }) => {
        const { startDate, timezoneIdentifier, timezoneShortName } =
          row.original;
        return formatSessionDateTime(
          startDate,
          timezoneIdentifier,
          timezoneShortName,
        );
      },
    },
    {
      header: "End Date",
      accessorKey: "endDate",
      enableSorting: true,
      cell: ({ row }) => {
        const { endDate, timezoneIdentifier, timezoneShortName } = row.original;
        return formatSessionDateTime(
          endDate,
          timezoneIdentifier,
          timezoneShortName,
        );
      },
    },
    {
      header: "Max Enrollments",
      accessorKey: "maxEnrollments",
      enableSorting: true,
      cell: ({ row }) => row.original.maxEnrollments ?? "--",
    },
    {
      header: "Published",
      accessorKey: "isPublished",
      enableSorting: true,
      cell: ({ row }) => {
        const { uuid, isPublished } = row.original;

        const stopPropagation = (evt: React.MouseEvent) => {
          evt.stopPropagation();
        };

        const handleChange = (checked: boolean) => {
          onTogglePublishedSession(uuid, checked);
        };

        return (
          <Switch
            checked={Boolean(isPublished)}
            onCheckedChange={handleChange}
            onClick={stopPropagation}
          />
        );
      },
    },
    {
      header: "Status",
      accessorKey: "isFinalized",
      enableSorting: true,
      cell: ({ row }) => {
        const { isFinalized, isCancelled } = row.original;

        if (isCancelled) {
          return (
            <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600">
              <XCircle className="h-3 w-3 mr-1" />
              Cancelled
            </Badge>
          );
        }

        if (isFinalized) {
          return (
            <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Finalized
            </Badge>
          );
        }

        return (
          <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600">
            <Clock className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      },
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => (
        <EventSessionActionMenu
          isCancelled={row.original.isCancelled}
          onCancelSession={onCancelSession}
          onDeleteSession={onDeleteSession}
          onEditSession={onEditSession}
          sessionUuid={row.original.uuid}
        />
      ),
    },
  ];
}
